import { useCallback, useEffect, useMemo, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import type { FieldConfig } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import type { InProgressIssue, OnTransformCreateFormProps } from '../../common/types.tsx';
import {
	transformClassificationLevelFieldProps,
	transformFieldConfigAndDirtyFieldsCurrentSession,
	transformStatusFieldProps,
} from './utils.tsx';

export function useRestoreFormFields(initialInProgressIssue?: InProgressIssue): {
	onTransformCreateFormProps: OnTransformCreateFormProps;
} {
	const { subscribe } = useGICLifeCycleEvents();

	const inProgressIssue = useRef<InProgressIssue>();
	const fieldConfig = useRef<FieldConfig>();

	useEffect(() => {
		inProgressIssue.current = initialInProgressIssue;
	}, [initialInProgressIssue]);

	const onTransformCreateFormProps: OnTransformCreateFormProps = useCallback((createFormProps) => {
		if (!inProgressIssue?.current) {
			return createFormProps;
		}
		if (inProgressIssue?.current?.formData && isEmpty(fieldConfig.current)) {
			const transformedData = transformFieldConfigAndDirtyFieldsCurrentSession(
				inProgressIssue?.current?.formData,
				inProgressIssue?.current?.formDataDefaultValues,
				createFormProps,
			);
			fieldConfig.current = transformedData?.fieldConfig;
		}

		return {
			...createFormProps,
			fieldConfig: fieldConfig.current || createFormProps.fieldConfig,
			classificationFieldProps: transformClassificationLevelFieldProps(
				inProgressIssue?.current?.classificationLevelField,
				createFormProps?.classificationFieldProps,
			),
			statusFieldProps: transformStatusFieldProps(
				inProgressIssue?.current?.statusField,
				createFormProps?.statusFieldProps,
			),
		};
	}, []);

	const onReset = useCallback(() => {
		inProgressIssue.current = undefined;
		fieldConfig.current = undefined;
	}, []);
	// TODO handle for multiple GIC
	useEffect(() => subscribe(GIC_LIFECYCLE_EVENTS.MODAL_CLOSED, onReset), [onReset, subscribe]);
	useEffect(
		() => subscribe(GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_EXISTING_IN_PROGRESS_ISSUE, onReset),
		[onReset, subscribe],
	);
	useEffect(
		() => subscribe(GIC_LIFECYCLE_EVENTS.PROJECT_FIELD_VALUE_CHANGE, onReset),
		[onReset, subscribe],
	);
	useEffect(
		() => subscribe(GIC_LIFECYCLE_EVENTS.ISSUE_TYPE_FIELD_VALUE_CHANGE, onReset),
		[onReset, subscribe],
	);

	const resp = useMemo(() => ({ onTransformCreateFormProps }), [onTransformCreateFormProps]);

	return resp;
}
